import { useRollbar } from '@rollbar/react'
import { useEffect } from 'react'
import { clarity as clarityClient } from 'react-microsoft-clarity'

interface ClarityIdentifyProps {
  clarityProjectId?: string
  uuid: string | null
  sid: string
}

// Polling safeguards ensure identify fires only after the Clarity SDK finishes loading.
const CLARITY_READY_INITIAL_DELAY_MS = 200
const CLARITY_READY_BACKOFF_FACTOR = 2
const CLARITY_READY_MAX_DELAY_MS = 2000
const CLARITY_READY_MAX_ATTEMPTS = 10

export function ClarityIdentify({ clarityProjectId, uuid, sid }: ClarityIdentifyProps) {
  const rollbar = useRollbar()

  useEffect(() => {
    if (!clarityProjectId || !uuid) {
      return
    }

    let attempts = 0
    let delay = CLARITY_READY_INITIAL_DELAY_MS
    let retryHandle: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const identifyWhenReady = () => {
      if (cancelled) {
        return
      }

      let clarityReady = false
      if (typeof clarityClient.hasStarted === 'function') {
        clarityReady = clarityClient.hasStarted()
      } else if (typeof window.clarity === 'function') {
        clarityReady = true
      }

      if (clarityReady && window.clarity) {
        window.clarity('identify', uuid, sid)
        return
      }

      attempts += 1

      if (attempts >= CLARITY_READY_MAX_ATTEMPTS) {
        rollbar?.info('Clarity identify skipped: SDK did not initialize in time', {
          uuid,
          sid,
          attempts,
          delayMs: delay,
          clarityProjectId,
        })
        return
      }

      retryHandle = setTimeout(identifyWhenReady, delay)
      delay = Math.min(delay * CLARITY_READY_BACKOFF_FACTOR, CLARITY_READY_MAX_DELAY_MS)
    }

    identifyWhenReady()

    return () => {
      cancelled = true
      if (retryHandle) {
        clearTimeout(retryHandle)
      }
    }
  }, [clarityProjectId, rollbar, uuid, sid])

  return null
}
